import React, { ReactNode, useState } from 'react'
import * as Yup from 'yup'
import { Alert, Button, Link, Stack, TextField } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { TypedFormik } from '@dai/components'

interface SendVerifyAndChangeEmailProps {
  signInUrl: string
  onCancel: () => void
  initialError?: string
  signOut: () => Promise<void>
  sendVerifyBeforeUpdateEmail: (email: string) => Promise<void>
}

const SendVerifyAndChangeEmail: React.FC<SendVerifyAndChangeEmailProps> = ({
  signInUrl,
  onCancel,
  initialError,
  signOut,
  sendVerifyBeforeUpdateEmail,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [updated, setUpdated] = useState<boolean>(false)

  const ERROR_MESSAGES: Record<string, ReactNode> = {
    'auth/requires-recent-login': (
      <>
        Updating your email requires a recent login. Please log in with your old
        email and try again.{' '}
        <Link
          onClick={async () => {
            await signOut()
            navigate(signInUrl, {
              state: {
                from: location.pathname + location.search,
              },
              replace: true,
            })
          }}
        >
          Log in
        </Link>
      </>
    ),
  }

  return (
    <TypedFormik
      initialValues={{
        email: '',
      }}
      initialStatus={
        initialError ? { success: false, message: initialError } : undefined
      }
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email('Must be a valid email')
          .max(255)
          .required('Email is required'),
      })}
      onSubmit={async (values, { setStatus }) => {
        try {
          await sendVerifyBeforeUpdateEmail(values.email)
          setStatus({
            success: true,
            message: `Verification email sent to ${values.email}. Please check your inbox and verify your email to finish updating it.`,
          })
          setUpdated(true)
        } catch (error: any) {
          const message =
            ERROR_MESSAGES[error.code] ||
            error.message ||
            'Something went wrong'
          setStatus({ success: false, message })
        }
      }}
    >
      {({
        status,
        errors,
        handleSubmit,
        isSubmitting,
        handleBlur,
        handleChange,
        values,
        touched,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            {status?.message && (
              <Alert severity={status.success ? 'success' : 'warning'}>
                {status.message}
              </Alert>
            )}
            <TextField
              type="email"
              name="email"
              label="Email address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                fullWidth
                color="primary"
                variant="outlined"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                Cancel
              </Button>
              {updated ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                  onClick={onCancel}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                  type="submit"
                >
                  Update
                </Button>
              )}
            </Stack>
          </Stack>
        </form>
      )
      }
    </TypedFormik >
  )
}

export default SendVerifyAndChangeEmail
